import { useFormikContext } from 'formik';
import { useState } from 'react';
import moment from 'moment';
import themes from '~/themes';
import { Stack, Typography } from '@mui/material';
import Button from '~/components/common/Button';
import AddIcon from '~/assets/images/icons/add-black.svg';
import { KeyboardArrowRight } from '@mui/icons-material';
import { formatSimpleDate, formatSimpleTime } from '~/utils/common';
import { IDriver } from '../../../types';
import DialogAddShiftSchedule from './DialogAddShiftSchedule';

const SpecialSchedule = () => {
  const { values, setFieldValue } = useFormikContext<IDriver>();
  const [openSpecial, setOpenSpecial] = useState(false);
  const [daySelected, setDaySelected] = useState<any>();

  const { delivery } = values;
  const { special_hours } = delivery;

  const getInValidDays = (specialSchedule) => {
    const dates = (specialSchedule || []).reduce((acc, item) => {
      let now = item.start_date ? moment(item.start_date) : null;
      const endM = item.end_date ? moment(item.end_date) : null;
      while (now && endM && now.isSameOrBefore(endM)) {
        acc[now.format('MM/DD/YYYY')] = true;
        now = now.add(1, 'days');
      }
      return acc;
    }, {});
    return Object.keys(dates);
  };

  const renderSlot = (date, idx) => {
    let content = null;

    if (!date?.available) {
      content = <Typography variant='body1'>Off shift</Typography>;
    } else if (date.all_hour) {
      content = <Typography variant='body1'>24 hours</Typography>;
    } else {
      content = (
        <Typography
          variant='body1'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {date?.shifts
            ?.map((shift) => {
              const start = formatSimpleTime(moment(shift.start_time, 'HH:mm'));
              const end = formatSimpleTime(moment(shift.end_time, 'HH:mm'));
              return `${start} - ${end}`;
            })
            .join(', ')}
        </Typography>
      );
    }

    return (
      <Stack
        key={idx}
        flexDirection='row'
        alignItems='center'
        gap={1}
        sx={{
          'padding': '0px 8px',
          '&:hover': {
            backgroundColor: '#E5E3F4',
            cursor: 'pointer',
          },
          'minHeight': 42,
        }}
        onClick={() => {
          setDaySelected({ ...date, idx });
          setOpenSpecial(true);
        }}
      >
        <Stack
          sx={{
            width: 120,
            [themes.breakpoints.down('md')]: {
              width: 'unset',
              flex: 1,
              overflow: 'hidden',
            },
          }}
        >
          <Typography
            variant='body1'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {date?.name}
          </Typography>
        </Stack>
        <Stack
          sx={{
            width: 220,
            [themes.breakpoints.down('md')]: {
              width: 'unset',
              flex: 1,
              overflow: 'hidden',
            },
          }}
        >
          <Typography
            variant='body1'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >{`${formatSimpleDate(moment(date?.start_date))} - ${formatSimpleDate(
            moment(date?.end_date),
          )}`}</Typography>
        </Stack>
        <Stack
          flex={1}
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          overflow='hidden'
          sx={{
            [themes.breakpoints.down('md')]: {
              width: 'unset',
              flex: 1,
              overflow: 'hidden',
            },
          }}
        >
          {content}
          <KeyboardArrowRight
            sx={{ color: `${themes.color.violet900} !important` }}
          />
        </Stack>
      </Stack>
    );
  };

  const renderTimeSlots = () => {
    if (special_hours?.length === 0) return null;

    return (
      <>
        <Stack flexDirection='row' gap={1} sx={{ padding: '6px 8px' }}>
          <Typography
            variant='caption'
            sx={{
              color: '#8C95BA',
              width: 120,
              [themes.breakpoints.down('md')]: {
                width: 'unset',
                flex: 1,
              },
            }}
          >
            Name
          </Typography>
          <Typography
            variant='caption'
            sx={{
              color: '#8C95BA',
              width: 220,
              [themes.breakpoints.down('md')]: {
                width: 'unset',
                flex: 1,
              },
            }}
          >
            Date
          </Typography>
          <Typography
            variant='caption'
            sx={{
              color: '#8C95BA',
              [themes.breakpoints.down('md')]: {
                width: 'unset',
                flex: 1,
              },
            }}
          >
            Shift hours
          </Typography>
        </Stack>
        {special_hours?.map((date, idx) => renderSlot(date, idx))}
      </>
    );
  };

  return (
    <Stack flexDirection='column' gap={1.8}>
      <Stack flexDirection='column' gap={0.8}>
        <Typography fontWeight={600} color={themes.color.black}>
          Special Hours
        </Typography>
        <Typography variant='caption'>
          Set special hours for an occasion (holiday, vacation, etc.)
        </Typography>
      </Stack>
      <Stack flexDirection='column'>
        {renderTimeSlots()}
        <Stack flexDirection='row' gap={1.6}>
          <Button
            noRounder
            buttonType='default'
            sx={{
              mt: 1.6,
              gap: '4px',
              width: 'fit-content',
            }}
            onClick={() => setOpenSpecial(true)}
          >
            <img src={AddIcon} alt='' />
            Special Hours
          </Button>
        </Stack>
      </Stack>
      {openSpecial && (
        <DialogAddShiftSchedule
          open
          onClose={() => {
            setOpenSpecial(false);
            setDaySelected(null);
          }}
          data={daySelected ? [daySelected] : []}
          isSpecial
          invalidDays={
            daySelected
              ? getInValidDays(special_hours).filter(
                  (e) => !getInValidDays([daySelected]).includes(e),
                )
              : getInValidDays(special_hours)
          }
          onSave={(val) => {
            let newVal = [...special_hours];
            if (!daySelected) {
              newVal = [...newVal, val];
            } else {
              newVal = special_hours?.map((day, idx) => {
                if (idx === daySelected.idx) {
                  return val;
                }
                return day;
              });
            }
            setFieldValue('delivery', {
              ...values.delivery,
              special_hours: newVal,
            });
            setDaySelected([]);
          }}
          onDelete={() => {
            let newVal = [...special_hours];
            newVal = newVal?.filter((_, idx) => idx !== daySelected.idx);
            setFieldValue('delivery', {
              ...values.delivery,
              special_hours: newVal,
            });
            setDaySelected([]);
          }}
        />
      )}
    </Stack>
  );
};

export default SpecialSchedule;
